/**
 * Enhanced Input Component
 *
 * Apple-inspired input field with proper semantic tokens
 * and beautiful animations following the design system.
 * Uses class-variance-authority for consistent styling.
 *
 * Features:
 * - Consistent sizing with design tokens
 * - Focus ring using --ring token
 * - Error state with aria-invalid
 * - Disabled state with proper styling
 * - Left/right icon support
 *
 * @example Basic usage
 *```tsx
 * <Input placeholder="Enter your email"/>
 *```
 *
 * @example With label association
 *```tsx
 * <Label htmlFor="email">Email</Label>
 * <Input id="email"type="email"/>
 *```
 *
 * @example With error state
 *```tsx
 * <Input
 * id="email"
 * error
 * aria-describedby="email-error"
 * />
 * <span id="email-error"role="alert">Invalid email</span>
 *```
 *
 * @example With FormField wrapper
 *```tsx
 * <FormField id="email"label="Email"error={errors.email}>
 * <Input type="email"/>
 * </FormField>
 *```
 */

import * as React from "react";
import type {VariantProps} from "class-variance-authority";
import {cn} from "@/shared/utils/utils.ts";
import {inputVariants} from "./Input.variants";

interface InputProps
 extends Omit<React.InputHTMLAttributes<HTMLInputElement>,"size">,
 VariantProps<typeof inputVariants> {
 /** Icon element to display on the left side */
 leftIcon?: React.ReactNode;
 /** Icon element to display on the right side */
 rightIcon?: React.ReactNode;
}

/**
 * Input component with consistent styling and accessibility.
 *
 * Automatically sets aria-invalid="true"when error prop is true.
 * Use with Label component for proper label association.
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
 (
 {
 className,
 type,
 variant,
 inputSize,
 error,
 leftIcon,
 rightIcon,
"aria-invalid": ariaInvalid,
 ...props
 },
 ref
 ): React.ReactElement => {
 // Set aria-invalid based on error prop if not explicitly provided
 const computedAriaInvalid = ariaInvalid ?? (error ?"true": undefined);

  const inputElement = (
    <input
      type={type}
      className={cn(
        inputVariants({ variant, inputSize, error }),
        leftIcon &&"pl-10",
        rightIcon &&"pr-10",
        className
      )}
      ref={ref}
      aria-invalid={computedAriaInvalid}
      {...props}
    />
  );

 if (leftIcon || rightIcon) {
 return (
 <div className="relative group">
 {leftIcon && (
 <div
 className={cn(
"absolute left-3 top-1/2 -translate-y-1/2",
"text-muted-foreground",
"transition-colors duration-200",
"group-focus-within:text-primary",
 error &&"text-destructive group-focus-within:text-destructive"
 )}
 aria-hidden="true"
 >
 {leftIcon}
 </div>
 )}
 {inputElement}
 {rightIcon && (
 <div
 className={cn(
"absolute right-3 top-1/2 -translate-y-1/2",
"text-muted-foreground",
"transition-colors duration-200",
"group-focus-within:text-primary",
 error &&"text-destructive group-focus-within:text-destructive"
 )}
 aria-hidden="true"
 >
 {rightIcon}
 </div>
 )}
 </div>
 );
 }

 return inputElement;
 }
);

Input.displayName ="Input";

export { Input };
